<template>
  <div class="app-list__root">
    <template v-if="!isDataInit">
      <app-list-skeleton></app-list-skeleton>
    </template>

    <div class="app-list__main" :class="[!isDataInit && 'skeleton-loading']">
      <template v-if="isGetDataFail">
        <app-list-error @on-refresh-click="refreshData"></app-list-error>
      </template>
      <template v-else-if="isDataInit && !appList.length">
        <app-list-empty></app-list-empty>
      </template>
      <template v-else>
        <div class="app-list-content">
          <app-blank-create></app-blank-create>
          <template v-if="appList.length">
            <app-card
              v-for="(app, index) in appList"
              :key="app.tpl_id + '_' + index"
              :seq="index"
              :appData="app"
              @on-app-click="onAppClick"
            ></app-card>
          </template>
        </div>
        <app-list-end class="app-list__footer"></app-list-end>

        <app-use v-if="isShowAppUse" :appData="appUseData" @on-app-use-modal-closed="onAppUseClosed"></app-use>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getTemplateTypeEmitter } from '@/features/template-type/template-type.tools';
import { usePTeamStore } from '@/store';
import { apiGetTpl_appBy_category, apiGetTpl_appSearch, RespCategory, RespTplApp } from '@vas2t-lowcode/api';
import { defineAsyncComponent, onMounted, ref, watchEffect } from 'vue';
import { getMyAppEmitter, MY_APP_EMITTER } from '../my-app/my-app.tools';
import AppCard from './app-card.vue';
import { reqDefAppListDataAsync, resetAppListDataAsync } from './app-list-interface';
import appListSkeleton from './app-list-skeleton.vue';
import { dwOnAppTemplateClick, dwOnAppTemplateShow } from './app-list.dw';
import { getSelectedTemplate } from './app-list.tool';
import AppBlankCreate from './app-blank-create.vue';

// const appListSkeleton = defineAsyncComponent(() => import('./app-list-skeleton.vue'));
// const appCard = defineAsyncComponent(() => import('./app-card.vue'));
const appListEnd = defineAsyncComponent(() => import('./app-list-end.vue'));
const appListError = defineAsyncComponent(() => import('./app-list-error.vue'));
const appListEmpty = defineAsyncComponent(() => import('./app-list-empty.vue'));
const appUse = defineAsyncComponent(() => import('./app-use.vue'));

const isDataInit = ref(false);

const isLoadingData = ref(false);

const isGetDataFail = ref(false);

/** 记录刷新重试 */
let retryFn = undefined as (() => void) | undefined;

const appList = ref<RespTplApp[]>([]);

/** 根据关键字获取应用列表 */
const getAppListByKey = (key = '') => {
  if (isLoadingData.value || !key) {
    return;
  }

  isLoadingData.value = true;
  const params = { key_word: key };
  return apiGetTpl_appSearch(params, {})
    .then(({ data }) => {
      appList.value = data;

      setGetSuccess();

      reloadSearchHistory();
    })
    .catch(() => {
      isGetDataFail.value = true;
    })
    .finally(() => {
      isLoadingData.value = false;
    });
};

const reloadSearchHistory = () => {
  const emitter = getMyAppEmitter(MY_APP_EMITTER);
  emitter.emit('on-reload-app-search-history');
};

/** 根据模板类型id获取应用列表 */
const getAppListByTemplateType = ({ id }: RespCategory) => {
  if (!id || isLoadingData.value) {
    return;
  }

  isLoadingData.value = true;
  const params = { category_id: id };
  return apiGetTpl_appBy_category(params)
    .then(({ data }) => {
      appList.value = data;

      setGetSuccess();
    })
    .catch(() => {
      isGetDataFail.value = true;
      setGetDataError(getAppListByTemplateType, { id });
    })
    .finally(() => {
      isDataInit.value = true;
      isLoadingData.value = false;
    });
};

const setGetDataError = (fn: (data: any) => unknown, data?: unknown) => {
  isGetDataFail.value = true;
  retryFn = () => {
    fn && fn(data);
  };
};

const setGetSuccess = () => {
  isGetDataFail.value = false;
  retryFn = undefined;
};

/** 刷新重试 */
const refreshData = () => {
  if (!isGetDataFail.value) {
    return;
  }

  retryFn && retryFn();
};

const bindEvent = () => {
  getTemplateTypeEmitter().on('on-app-search-by-key', getAppListByKey);

  getTemplateTypeEmitter().on('on-template-type-click', getAppListByTemplateType);
};

/**
 * 获取首屏展示的模板列表
 */
const getDefAppList = () => {
  if (isLoadingData.value) {
    return;
  }

  isLoadingData.value = true;
  return reqDefAppListDataAsync()
    ?.then(({ data }) => {
      appList.value = data;

      setGetSuccess();
    })
    .catch(() => {
      isGetDataFail.value = true;
      setGetDataError(getDefAppList);
    })
    .finally(() => {
      resetAppListDataAsync();
      isDataInit.value = true;
      isLoadingData.value = false;
    });
};

const initData = () => {
  getDefAppList();
};

// 埋点逻辑
const teamStore = usePTeamStore();
watchEffect(() => {
  if (isDataInit.value && appList.value.length && teamStore.isInitStaticsData) {
    dwOnAppTemplateShow();
  }
});

/** 使用app弹窗 */
const isShowAppUse = ref(false);
const appUseData = ref<RespTplApp>({});
const onAppClick = (data: RespTplApp) => {
  isShowAppUse.value = true;
  appUseData.value = data;
};
const onAppUseClosed = () => {
  isShowAppUse.value = false;
  // appUseData.value = {};
};

onMounted(() => {
  bindEvent();

  initData();
});
</script>

<style lang="scss" scoped>
.app-list {
  &__root {
    min-width: var(--lowcode-min-width);
    position: relative;
    width: 100%;
    // padding: 9px 24px 0 24px;
    box-sizing: border-box;
    // display: flex;
    // flex-direction: column;
    flex-grow: 1;
    flex-shrink: 0;
  }

  &__main {
    height: 100%;
    display: grid;
    grid-template-rows: auto 1fr auto;
    grid-template-areas:
      'app-list-head'
      'space'
      'app-list-end';
  }

  &-content {
    grid-area: app-list-head;
    // flex-grow: 1;
    // display: flex;
    // flex-wrap: wrap;
    // align-content: flex-start;

    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(262px, 1fr));
    grid-template-rows: repeat(auto-fill, 139px);
    row-gap: 16px;
    column-gap: 16px;
  }

  &__footer {
    grid-area: app-list-end;
  }
}
</style>
